/**
 * StatusPeek — per `rcp-status-peek.html`.
 *
 * Shows the current cart's stage + timeline. Customer-friendly rollup of
 * PipelineStepExecutions.
 *
 * A11y fix (mockup #12):
 *   - Timeline steps NOT color-only — every step has a shape (check / dot /
 *     exclamation) + a text label (done / current / upcoming / blocked).
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import type { StatusPeekDto } from '@retail-ops/shared-types';
import { getApiClient } from '../../services/apiClient';
import { useToast } from '../../components/a11y/AccessibleToast';
import { Check, Circle, AlertOctagon, Clock } from '@retail-ops/panel-shell/fi';

type StepState = 'done' | 'current' | 'upcoming' | 'blocked';

export function StatusPeek(): JSX.Element {
  const { cartId } = useParams<{ cartId: string }>();
  const { pushToast } = useToast();
  const [status, setStatus] = useState<StatusPeekDto | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!cartId) return undefined;
    const controller = new AbortController();
    (async () => {
      try {
        // TODO(PLANNED /portal/status/{cartId}).
        const res = await getApiClient().portal.status.peek(cartId, { signal: controller.signal });
        if (res.success && res.data) setStatus(res.data);
        else pushToast('error', 'Could not load status', res.error ?? 'Please refresh.');
      } catch (e) {
        if ((e as Error)?.name !== 'AbortError') {
          pushToast('error', 'Could not load status', 'Please refresh the page.');
        }
      } finally {
        setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [cartId, pushToast]);

  if (loading) return <p aria-live="polite">Loading your application status...</p>;
  if (!status) {
    return (
      <section aria-labelledby="status-heading">
        <h1 id="status-heading" tabIndex={-1}>Application not found</h1>
        <p>We couldn&apos;t find that application. It may have been completed or cancelled.</p>
      </section>
    );
  }

  const history = status.stageHistory ?? [];

  return (
    <section aria-labelledby="status-heading">
      <h1 id="status-heading" tabIndex={-1}>Your application status</h1>
      <p className="lead">
        Reference: <span className="mono">{status.cartId}</span>
      </p>

      <div className="card">
        <h2>Current stage</h2>
        <p style={{ fontSize: 18, fontWeight: 500, color: 'var(--pcb-blue-dark)' }}>
          {status.currentStage ?? status.status}
        </p>
        {status.nextAction ? (
          <p style={{ color: 'var(--ink-700)' }}>
            <strong>Next:</strong> {status.nextAction}
          </p>
        ) : null}
      </div>

      <div className="card">
        <h2>Timeline</h2>
        <ol className="timeline" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {history.map((h, idx) => {
            const isCurrent = !h.completedAt;
            const state: StepState = isCurrent ? 'current' : 'done';
            return <TimelineRow key={idx} label={h.stage} when={h.completedAt ?? h.enteredAt} state={state} />;
          })}
          {history.length === 0 ? (
            <TimelineRow label={status.currentStage ?? status.status} when={undefined} state="current" />
          ) : null}
        </ol>
      </div>
    </section>
  );
}

function TimelineRow({
  label,
  when,
  state,
}: {
  label: string;
  when: string | undefined;
  state: StepState;
}): JSX.Element {
  const Icon =
    state === 'done' ? Check
      : state === 'current' ? Clock
        : state === 'blocked' ? AlertOctagon
          : Circle;
  const stateText =
    state === 'done' ? 'Completed'
      : state === 'current' ? 'In progress'
        : state === 'blocked' ? 'Blocked'
          : 'Upcoming';
  return (
    <li className="timeline-step" data-state={state} aria-label={`${label} — ${stateText}`}>
      <span className="timeline-marker" aria-hidden="true">
        <Icon size={16} />
      </span>
      <div>
        <div className="timeline-label">{label}</div>
        <div className="timeline-sub">
          <span style={{ fontWeight: 500 }}>{stateText}</span>
          {when ? <> &middot; {new Date(when).toLocaleString()}</> : null}
        </div>
      </div>
    </li>
  );
}
